import React, { useMemo } from 'react';
import { Persona } from '../../types/persona';

interface PersonaStatsProps {
  personas: Persona[];
}

export const PersonaStats: React.FC<PersonaStatsProps> = ({ personas }) => {
  const stats = useMemo(() => {
    if (personas.length === 0) {
      return {
        total: 0,
        averageAge: 0,
        genderDistribution: {},
        brandRelationships: {},
        panelType: 'Unknown',
      };
    }

    // Calculate average age
    const totalAge = personas.reduce((sum, p) => sum + p.demographics.age, 0);
    const averageAge = Math.round(totalAge / personas.length);

    // Gender distribution
    const genderDistribution: Record<string, number> = {};
    personas.forEach((p) => {
      const gender = p.demographics.gender;
      genderDistribution[gender] = (genderDistribution[gender] || 0) + 1;
    });

    // Brand relationship distribution
    const brandRelationships: Record<string, number> = {};
    personas.forEach((p) => {
      const status = p.brand_relationship.usage_status;
      brandRelationships[status] = (brandRelationships[status] || 0) + 1;
    });

    // Determine panel type based on professional context
    const hasB2B = personas.some((p) => p.professional_context);
    const panelType = hasB2B ? 'B2B' : 'Brand/Category';

    return {
      total: personas.length,
      averageAge,
      genderDistribution,
      brandRelationships,
      panelType,
    };
  }, [personas]);

  if (personas.length === 0) {
    return null;
  }

  // Color coding for brand relationships
  const getBrandColor = (status: string): string => {
    const lower = status.toLowerCase();
    if (lower.includes('superfan') || lower.includes('super fan')) return 'bg-green-500';
    if (lower.includes('user') || lower.includes('regular')) return 'bg-blue-500';
    if (lower.includes('casual') || lower.includes('occasional')) return 'bg-gray-500';
    if (lower.includes('skeptic') || lower.includes('aware')) return 'bg-orange-500';
    if (lower.includes('non-user') || lower.includes('nonuser') || lower.includes('never')) return 'bg-red-500';
    return 'bg-gray-500';
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-6">Panel Overview</h3>

      {/* Key Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {/* Total Personas */}
        <div className="bg-primary-50 rounded-lg p-4">
          <div className="text-sm text-primary-500 font-medium mb-1">Total Personas</div>
          <div className="text-3xl font-bold text-indigo-900">{stats.total}</div>
        </div>

        {/* Panel Type */}
        <div className="bg-purple-50 rounded-lg p-4">
          <div className="text-sm text-secondary-600 font-medium mb-1">Panel Type</div>
          <div className="text-2xl font-bold text-purple-900">{stats.panelType}</div>
        </div>

        {/* Average Age */}
        <div className="bg-blue-50 rounded-lg p-4">
          <div className="text-sm text-blue-600 font-medium mb-1">Average Age</div>
          <div className="text-3xl font-bold text-blue-900">{stats.averageAge}</div>
        </div>

        {/* Gender Split */}
        <div className="bg-green-50 rounded-lg p-4">
          <div className="text-sm text-accent-600 font-medium mb-1">Gender Split</div>
          <div className="text-lg font-bold text-green-900">
            {Object.entries(stats.genderDistribution)
              .map(([gender, count]) => `${gender}: ${count}`)
              .join(' / ')}
          </div>
        </div>
      </div>

      {/* Brand Relationships Distribution */}
      <div>
        <h4 className="text-sm font-medium text-gray-700 mb-3">Brand Relationships</h4>
        <div className="space-y-3">
          {Object.entries(stats.brandRelationships)
            .sort((a, b) => b[1] - a[1])
            .map(([status, count]) => {
              const percentage = Math.round((count / stats.total) * 100);
              return (
                <div key={status}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-700 font-medium">{status}</span>
                    <span className="text-gray-500">
                      {count} ({percentage}%)
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full ${getBrandColor(status)} transition-all`}
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                </div>
              );
            })}
        </div>
      </div>

      {/* Cognitive Averages */}
      <div className="mt-6 pt-6 border-t border-gray-200">
        <h4 className="text-sm font-medium text-gray-700 mb-3">Cognitive Averages</h4>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div>
            <div className="text-xs text-gray-500 mb-1">Avg IQ</div>
            <div className="text-xl font-bold text-gray-900">
              {Math.round(
                personas.reduce((sum, p) => sum + p.cognitive.iq, 0) / personas.length
              )}
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-500 mb-1">Avg Critical Thinking</div>
            <div className="text-xl font-bold text-gray-900">
              {Math.round(
                personas.reduce((sum, p) => sum + p.cognitive.critical_thinking, 0) /
                  personas.length
              )}
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-500 mb-1">Avg Specific Knowledge</div>
            <div className="text-xl font-bold text-gray-900">
              {Math.round(
                personas.reduce((sum, p) => sum + p.cognitive.knowledge_specific, 0) /
                  personas.length
              )}
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-500 mb-1">Avg Category Knowledge</div>
            <div className="text-xl font-bold text-gray-900">
              {Math.round(
                personas.reduce((sum, p) => sum + p.cognitive.knowledge_category, 0) /
                  personas.length
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
